import React, { useState } from "react";
import { DemoLoginHelp } from "./Demo";
import { login } from "../api/client";
import { User } from "../types";

interface Props { onLogin: (user: User) => void; }

export default function Login({ onLogin }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock]         = useState(false);
  const [showHelp, setShowHelp]         = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(username.trim(), password);
      onLogin(res.data.user);
    } catch (err: any) {
      // Say what actually went wrong: a lockout or an unreachable server is not
      // a wrong password, and retyping it would not help.
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many failed sign-in attempts. Wait a minute, then try again.");
      } else if (!err?.response) {
        setError("Can't reach the server. Check your connection and try again.");
      } else if (status >= 500) {
        setError("The server had a problem signing you in. Try again in a moment.");
      } else {
        setError("Invalid username or password.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Caps Lock is the usual cause of a "wrong" password that was typed correctly.
  const trackCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(e.getModifierState?.("CapsLock") ?? false);

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* The Regreports mark (droplet + check), same artwork as regreports.com. */}
        <svg style={s.logo} viewBox="0 0 24 24" role="img" aria-label="Regreports logo">
          <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="#3b5bdb" />
          <path d="M12 5.6C12 5.6 16.8 10.8 16.8 13.9A4.8 4.8 0 0 1 7.2 13.9C7.2 10.8 12 5.6 12 5.6Z" fill="#fff" />
          <path d="M10.2 13.7l1.5 1.5 2.6-3.1" fill="none" stroke="#3b5bdb" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h1 style={s.title}>Regreports PIMS</h1>
        <p style={s.sub}>Pretreatment Information Management System</p>
        <form onSubmit={handleSubmit}>
          <label style={s.label} htmlFor="username">Username</label>
          <input id="username" name="username" style={s.input} value={username}
            autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            onChange={e => setUsername(e.target.value)} autoFocus required />

          <label style={s.label} htmlFor="password">Password</label>
          <div style={s.pwWrap}>
            <input id="password" name="password" style={{ ...s.input, ...s.pwInput }}
              type={showPassword ? "text" : "password"} value={password}
              autoComplete="current-password"
              onChange={e => setPassword(e.target.value)}
              onKeyDown={trackCapsLock} onKeyUp={trackCapsLock}
              onBlur={() => setCapsLock(false)} required />
            <button type="button" style={s.pwToggle}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword(v => !v)}>
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          {capsLock && <p style={s.hint} role="status">Caps Lock is on.</p>}

          {error && <p style={s.error} role="alert">{error}</p>}
          <button style={{ ...s.btn, ...(loading ? s.btnBusy : {}) }} type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <button type="button" style={s.link} aria-expanded={showHelp}
          onClick={() => setShowHelp(v => !v)}>
          Forgot your password?
        </button>
        <DemoLoginHelp onPick={(u, p) => { setUsername(u); setPassword(p); setError(""); }} />
        {showHelp && (
          <div style={s.help}>
            <p style={s.helpP}>
              Passwords are reset by an administrator at your Control Authority, not by email.
            </p>
            <ol style={s.helpList}>
              <li>Contact your Control Authority's pretreatment program administrator and ask for a password reset.</li>
              <li>They will give you a temporary password.</li>
              <li>Sign in with it here, and you will be asked to choose a new password right away.</li>
            </ol>
            <p style={s.helpP}>
              Forgot your username? The same administrator can look it up.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:  { display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#ebf8ff", padding:16 },
  card:  { background:"#fff", borderRadius:10, padding:"40px 48px", width:380, maxWidth:"100%", boxSizing:"border-box",
           boxShadow:"0 4px 24px rgba(0,0,0,0.10)" },
  logo:  { display:"block", width:52, height:52, marginBottom:14 },
  title: { color:"#1a365d", fontSize:28, fontWeight:700, marginBottom:4 },
  sub:   { color:"#4a5568", fontSize:14, marginBottom:28 },
  label: { display:"block", fontSize:13, fontWeight:600, color:"#2d3748", marginBottom:4 },
  input: { display:"block", width:"100%", padding:"8px 10px", marginBottom:16, boxSizing:"border-box",
           border:"1px solid #cbd5e0", borderRadius:5, fontSize:14 },
  pwWrap:   { position:"relative", marginBottom:16 },
  pwInput:  { paddingRight:64, marginBottom:0 },
  pwToggle: { position:"absolute", top:0, bottom:0, right:0, padding:"0 12px",
              background:"transparent", border:"none", borderRadius:"0 5px 5px 0",
              color:"#2b6cb0", fontSize:13, fontWeight:600, cursor:"pointer" },
  hint:  { color:"#b7791f", fontSize:13, margin:"-8px 0 12px" },
  btn:   { width:"100%", padding:"10px 0", background:"#2b6cb0", color:"#fff",
           border:"none", borderRadius:5, fontSize:15, fontWeight:600, cursor:"pointer" },
  btnBusy: { opacity:0.7, cursor:"wait" },
  error: { color:"#c53030", fontSize:13, marginBottom:10 },
  link:  { display:"block", margin:"16px auto 0", background:"none", border:"none", padding:0,
           color:"#2b6cb0", fontSize:13, cursor:"pointer", textDecoration:"underline" },
  help:  { marginTop:12, padding:"12px 14px", background:"#f7fafc", border:"1px solid #e2e8f0",
           borderRadius:6, color:"#2d3748", fontSize:13, lineHeight:1.5 },
  helpP: { margin:"0 0 8px" },
  helpList: { margin:"0 0 8px", paddingLeft:20 },
};
